import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Subject, Subscription } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { ApWindowService } from '@ap/cdk';
import { ApContentService, ContentPrefix } from '@ap/content';
import { ContractDetails } from '@ap/interface-apix-mijn-overzicht';
import { MijnLaunchDarklyService } from '@ap/mijn-kanaalconfiguratie';
import { MijnNavigatieService, MijnRouteKey } from '@ap/mijn-navigatie';
import { ApModalComponent, ApModalService, ModalOptions, ModalSize } from '@ap/modal';

import { ChatNotification, CONVERSATIONAL_CONFIGURATION_NAME } from '../mijn-overzicht-template';
import content from '../mijn-template.content.json';
import { MijnTemplateNavigation } from '../mijn-template.navigation';
import { MijnTemplateService, XC_HEADLESS_CONTENT } from '../mijn-template.service';

import { ContextHelpModalComponent } from './context-help-modal/context-help-modal.component';
import { ContextHelpModalOptions } from './context-help-modal/context-help-modal.model';

/**
 * @module MijnActieTemplateModule
 * @ngdoc component
 * @name MijnActieTemplateComponent
 */
@Component({
    selector: 'mijn-actie-template',
    styleUrls: ['./mijn-actie-template.component.scss'],
    templateUrl: './mijn-actie-template.component.html',
    standalone: false
})
export class MijnActieTemplateComponent implements OnChanges, OnInit, OnDestroy {
    @Input()
    public title: string;

    @Input()
    public tabTitle: string;

    @Input()
    public subtitle: string;

    @Input()
    public fullWidth = false;

    @Input()
    public dynamicRouting = false;

    @Input()
    public contextHelp: string = null;

    @Input()
    public showCloseButton: boolean = true;

    @Input()
    public changeConversationalStatus$: Subject<string>;

    @Output()
    public closeTemplate: EventEmitter<void> = new EventEmitter<void>();

    @Output()
    public chatNotificationSent = new EventEmitter<ChatNotification>();

    private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
    private readonly contentService: ApContentService = inject(ApContentService);
    private readonly launchdarklyService: MijnLaunchDarklyService = inject(MijnLaunchDarklyService);
    private readonly mijnNavigatieService: MijnNavigatieService = inject(MijnNavigatieService);
    private readonly mijnTemplateService: MijnTemplateService = inject(MijnTemplateService);
    private readonly mijnTemplateNavigation: MijnTemplateNavigation = inject(MijnTemplateNavigation);
    private readonly modalService: ApModalService = inject(ApModalService);
    private readonly windowService: ApWindowService = inject(ApWindowService);
    private readonly browserTabTitle: Title = inject(Title);

    public reload = false;
    public conversationalEnabled: boolean;
    private modalComponent: ApModalComponent;
    private contractDetails: ContractDetails;
    private readonly subscriptions = new Subscription();

    @HostListener('window:popstate')
    public onPopState(): void {
        if (this.modalComponent) {
            this.modalComponent.cancel();
        }
    }

    @HostListener('document:keydown.escape', ['$event'])
    handleEscapeKey(event: Event) {
        const target = event.target as HTMLElement;
        const isInsideModal = target.closest('ap-modal');
        if (isInsideModal) {
            return;
        }
        this.close();
    }

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.subscriptions.add(
            this.mijnTemplateService
                .getContractDetails()
                .pipe(
                    tap((contractDetails) => (this.contractDetails = contractDetails)),
                    switchMap((contractDetails) =>
                        this.mijnTemplateService.getHeadlessContent(XC_HEADLESS_CONTENT, contractDetails.is_zakelijk)
                    )
                )
                .subscribe((headlessContent) => {
                    if (headlessContent) {
                        this.reload = true;
                        this.contentService.removeContentItems(content);
                        this.contentService.addHeadlessContentItems(headlessContent);
                        this.changeDetector.detectChanges();
                        this.reload = false;
                    } else {
                        this.contextHelp = null;
                    }
                    if (this.contractDetails.is_zakelijk) {
                        this.contentService.setContentPrefix(ContentPrefix.BUSINESS);
                    }
                })
        );
        if (this.dynamicRouting) {
            this.mijnTemplateService.storeUrl(null, this.title);
        }
        if (this.tabTitle) {
            this.browserTabTitle.setTitle(this.tabTitle);
        }

        this.subscriptions.add(
            this.launchdarklyService.getFlagValue(CONVERSATIONAL_CONFIGURATION_NAME, false).subscribe((conversationalEnabled) => {
                this.conversationalEnabled = conversationalEnabled;
            })
        );
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.tabTitle) {
            this.browserTabTitle.setTitle(this.tabTitle);
        }
    }

    public isModalOpen(): boolean {
        if (document.querySelector('ap-modal')) {
            return true;
        }
        const dialogs = Array.from(document.querySelectorAll('dialog'));
        return dialogs.some((dialog) => dialog.open);
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public close(): void {
        if (this.dynamicRoutingAvailable()) {
            this.mijnTemplateNavigation.dynamicBack();
        } else if (this.closeTemplate.observed) {
            this.closeTemplate.emit();
        } else if (this.windowService.getWindow().history.length > 1) {
            this.windowService.getWindow().history.back();
        } else {
            this.mijnNavigatieService.navigate(MijnRouteKey.OVERVIEW);
        }
    }

    public onContextHelp(event: Event): void {
        event.preventDefault();

        // TODO: MO2-19136 analytics toevoegen
        const modalOptions: ModalOptions<ContextHelpModalOptions> = new ModalOptions({
            helpContext: this.contextHelp,
            ...(this.changeConversationalStatus$ && { openChat: () => this.changeConversationalStatus$.next('open') })
        });
        modalOptions.title = this.contentService.get('mijn.actie.template.contextHelpModal.title');
        modalOptions.size = ModalSize.MEDIUM;

        this.modalComponent = this.modalService.open(ContextHelpModalComponent, modalOptions);
        this.subscriptions.add(
            this.modalComponent.afterClosed().subscribe(() => {
                this.handleModalClosedReason();
                this.modalComponent = null;
            })
        );
    }

    private handleModalClosedReason(): void {
        // TODO: MO2-19136 analytics toevoegen
    }

    private dynamicRoutingAvailable(): boolean {
        return this.dynamicRouting && this.mijnTemplateNavigation.isMijnUrl() && this.mijnTemplateNavigation.hasDynamicRoute();
    }
}
